import { Checkbox, Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  TextField,
  TypableDropdown,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';

import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import componentConstants from '../../../core-utils/messages/componentConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import AddInteractionModal from '../AddInteractionModal';

export const cellStyles4 = {
  color: patientAccessTheme.palette.text.mediumemphasis,
  fontSize: '14px',
  fontWeight: 400,
};

export interface MemberOfInterestModalProps {
  open?: boolean;
  handleClose?: () => any;
  handleSave: () => any;
  isSaveButtonDisabled: boolean;
  moiDataState: [any, React.Dispatch<React.SetStateAction<any>>];
  typeOfTriggers: string[];
  isCareNavigator?: boolean;
  referrals?: string[];
  isMOITab: boolean;
}

const MemberOfInterestModal: React.FC<MemberOfInterestModalProps> = ({
  open,
  handleClose,
  handleSave,
  isSaveButtonDisabled,
  moiDataState,
  typeOfTriggers,
  isCareNavigator,
  referrals,
  isMOITab,
}) => {
  const useStyles = makeStyles()({
    modal: {
      width: '553px',
      height: isMOITab ? '800px' : isCareNavigator ? '600px' : '420px',
      background: patientAccessTheme.palette.status.white,
      borderRadius: '8px',
      padding: '24px 24px 24px 24px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      flexDirection: 'column',
      ...(isMOITab && {
        width: '800px',
        height: '600px',
        position: 'relative',
        flexDirection: 'column',
        top: 'none',
        left: 'none',
        transform: 'none',
      }),
    },
    inputFieldsGrid: {
      width: '505px',
      marginTop: '20px',
      background: '#FBFBFE',
      padding: '24px 24px 14px 24px',
      borderRadius: '8px',
      overflow: 'auto',
      ...(isCareNavigator && {
        height: '400px',
        '&::-webkit-scrollbar': {
          width: '5px',
          borderRadius: '12px',
          backgroundColor: patientAccessTheme.palette.primary.light,
        },
        '&::-webkit-scrollbar-track': {
          background: patientAccessTheme.palette.grey[300],
          borderRadius: '12px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: patientAccessTheme.palette.grey[500],
          borderRadius: '12px',
        },
      }),
      ...(isMOITab && {
        width: '750px',
        overflow: 'none',
      }),
    },
    selectFields: {
      display: 'flex',
      flexDirection: 'row',
      marginBottom: '10px',
    },
    cancelButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border: '1px solid ' + patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    saveButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border:
        '1px solid ' + isSaveButtonDisabled
          ? patientAccessTheme.palette.grey[500]
          : patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    checkBoxes: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '10px',
      ...(isMOITab && {
        width: '500px',
      }),
    },
    checkbox: {
      '&.MuiCheckbox-root': {
        padding: '0px',
      },

      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
      },
    },
  });
  const { classes } = useStyles();

  const inputWidth = isMOITab ? '320px' : '213px';

  const constants =
    componentConstants.ORGANISMS.CARE_NAV_MODALS.MEMBER_OF_INTEREST;

  const [moiData, setMoiData] = moiDataState;

  const handleCheckBoxes = (field: any) => {
    if (field === 'close' && moiData.interaction && moiData.selfAssign) {
      setMoiData((prev: any) => ({
        ...prev,
        close: !moiData.close,
      }));
      return;
    }
    if (field === 'interaction' && moiData.selfAssign) {
      setMoiData((prev: any) => ({
        ...prev,
        interaction: !moiData.interaction,
        close: false,
      }));
      return;
    }
    if (field === 'selfAssign') {
      setMoiData((prev: any) => ({
        ...prev,
        selfAssign: !moiData.selfAssign,
        interaction: false,
        close: false,
      }));
    }
    return;
  };

  const modalOrTab = (children: any) => {
    return isMOITab ? (
      <>{children}</>
    ) : (
      <Modal open={open || false} onClose={handleClose}>
        {children}
      </Modal>
    );
  };

  return modalOrTab(
    <Grid container className={classes.modal}>
      <Grid>
        <Typography
          fontSize="18px"
          lineHeight="26px"
          fontType="semibold"
          textColor={patientAccessTheme.palette.text.highemphasis}
          margin="4px 0px 0px 0px"
        >
          {constants.TITLE}
        </Typography>
        <Typography
          fontSize="14px"
          lineHeight="24px"
          fontType="normal"
          textColor={patientAccessTheme.palette.text.mediumemphasis}
          margin="4px 0px 5px 0px"
        >
          {constants.SUB_TITLE}
        </Typography>
      </Grid>

      <Grid container className={classes.inputFieldsGrid}>
        <Grid className={classes.selectFields}>
          <Grid style={{ marginRight: '30px' }}>
            <ReportCreateNewLabelField
              label={constants.NAME}
              component={
                <TextField
                  value={moiData.patient_name}
                  error={false}
                  placeholder={'yyyy zzzz'}
                  height="36px"
                  width={inputWidth}
                  inputPropsStyle={{
                    padding: '16px',
                  }}
                  disabled={true}
                />
              }
            />
          </Grid>
          <Grid>
            <ReportCreateNewLabelField
              label={constants.GROUP}
              component={
                <Dropdown
                  label={'Select'}
                  dropDownOptions={[moiData.group_name]}
                  dropDownValues={[moiData.group_name]}
                  value={moiData.group_name}
                  disabled={true}
                  selectStyles={{
                    ...commonDropDownProps.selectStyles,
                    width: inputWidth,
                  }}
                  labelTop="10px"
                  isTooltip={true}
                  tooltipValues={[]}
                />
              }
            />
          </Grid>
        </Grid>

        <Grid className={classes.selectFields}>
          <Grid style={{ marginRight: '30px' }}>
            <ReportCreateNewLabelField
              label={constants.PRIORITY}
              component={
                <Dropdown
                  label={'Select'}
                  dropDownOptions={['High', 'Medium', 'Low']}
                  dropDownValues={['High', 'Medium', 'Low']}
                  value={moiData.priority}
                  setValue={null}
                  handleChange={(e) => {
                    setMoiData((prev: any) => ({
                      ...prev,
                      priority: e.target.value,
                    }));
                  }}
                  selectStyles={{
                    ...commonDropDownProps.selectStyles,
                    width: inputWidth,
                  }}
                  labelTop="10px"
                />
              }
            />
          </Grid>
          <Grid>
            <ReportCreateNewLabelField
              label={constants.TRIGGER}
              component={
                <TypableDropdown
                  dropDownOptions={typeOfTriggers}
                  dropDownValues={typeOfTriggers}
                  placeholder={'Type of Trigger'}
                  value={moiData.type_of_trigger}
                  handleChange={(event) => {
                    const value = event?.target?.value as string;
                    setMoiData((prev: any) => ({
                      ...prev,
                      type_of_trigger: value,
                    }));
                  }}
                  handleInputChange={(value) => {
                    setMoiData((prev: any) => ({
                      ...prev,
                      type_of_trigger: value,
                    }));
                  }}
                  disabled={false}
                  textFieldPlaceholder="Enter type of trigger here (or) select from below"
                  height="36px"
                  width={inputWidth}
                  searchable={true}
                />
              }
            />
          </Grid>
        </Grid>

        {isCareNavigator && (
          <Grid className={classes.checkBoxes}>
            <Grid className={classes.selectFields}>
              <Checkbox
                className={classes.checkbox}
                color="primary"
                onChange={() => handleCheckBoxes('selfAssign')}
                checked={moiData.selfAssign}
                disableRipple={true}
                icon={
                  <CheckBoxOutlineBlankOutlinedIcon
                    style={{
                      color: patientAccessTheme.palette.checkbox?.border1,
                    }}
                  />
                }
              />
              <Typography
                textColor={patientAccessTheme.palette.text.highemphasis}
                fontSize="12px"
                lineHeight="20px"
                fontType={'normal'}
                margin="0px 0px 0px 12px"
              >
                {constants.selfAssign}
              </Typography>
            </Grid>

            <Grid className={classes.selectFields}>
              <Checkbox
                className={classes.checkbox}
                color="primary"
                onChange={() => handleCheckBoxes('interaction')}
                checked={moiData.interaction}
                disableRipple={true}
                icon={
                  <CheckBoxOutlineBlankOutlinedIcon
                    style={{
                      color: patientAccessTheme.palette.checkbox?.border1,
                    }}
                  />
                }
              />
              <Typography
                textColor={patientAccessTheme.palette.text.highemphasis}
                fontSize="12px"
                lineHeight="20px"
                fontType={'normal'}
                margin="0px 0px 0px 12px"
              >
                {constants.addInteraction}
              </Typography>
            </Grid>

            <Grid className={classes.selectFields}>
              <Checkbox
                className={classes.checkbox}
                color="primary"
                onChange={() => handleCheckBoxes('close')}
                checked={moiData.close}
                disableRipple={true}
                icon={
                  <CheckBoxOutlineBlankOutlinedIcon
                    style={{
                      color: patientAccessTheme.palette.checkbox?.border1,
                    }}
                  />
                }
              />
              <Typography
                textColor={patientAccessTheme.palette.text.highemphasis}
                fontSize="12px"
                lineHeight="20px"
                fontType={'normal'}
                margin="0px 0px 0px 12px"
              >
                {constants.closeCase}
              </Typography>
            </Grid>
          </Grid>
        )}

        {moiData.interaction && (
          <AddInteractionModal
            onlyInputs={true}
            dataState={[moiData, setMoiData]}
            referrals={referrals ?? []}
            inputWidth={inputWidth}
          />
        )}

        <Grid
          container
          style={{
            position: isMOITab ? 'relative' : 'absolute',
            bottom: 0,
            margin: '0px 0px 24px 210px',
          }}
          sx={{
            alignItems: 'center',
          }}
        >
          <Grid style={{ marginRight: '12px' }}>
            <Button
              label={constants.CANCEL}
              variant="outlined"
              className={classes.cancelButton}
              onClick={handleClose}
            />
          </Grid>
          <Grid>
            <Button
              label={constants.SUBMIT}
              variant="contained"
              className={classes.saveButton}
              disabled={isSaveButtonDisabled}
              onClick={() => {
                handleSave && handleSave();
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>,
  );
};
export default MemberOfInterestModal;
